import TitleBlock, { TitleDescription } from '@anm/components/TitleBlock';
import Button from '@anm/components/buttons/Button';
import IconButton from '@anm/components/buttons/IconButton';
import Image from '@anm/components/image/Image';
import { flexColumn, flexRow, media, truncateMultiline } from '@anm/styles/helpers';
import styled, { css } from 'styled-components';

type StreamTextItemVariants = 'small-grey' | 'big-black';

type StreamTextItemProps = {
  variant: StreamTextItemVariants;
};

const smallGreyTextMobile = media.phone`
  font-size: 13px;
  text-align: left;
`;
const bigBlackTextMobile = media.phone`
  font-size: 14px;
  text-align: left;
`;

const streamItemVariants: {
  [key in StreamTextItemVariants]: ReturnType<typeof css>;
} = {
  'small-grey': css`
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: var(--gray_400);
    ${smallGreyTextMobile};
  `,
  'big-black': css`
    font-size: 16px;
    line-height: 22px;
    color: var(--gray_900);
    ${bigBlackTextMobile};
  `
};

export const StreamTextItem = styled.div<StreamTextItemProps>`
  text-align: right;
  min-width: fit-content;
  ${({ variant }) => streamItemVariants[variant]};
`;

export const PreviewWrapper = styled.div`
  grid-area: preview;
  max-height: 90px;
  margin-right: 24px;
  height: min-content;
  background: var(--gray_50);
`;

export const Preview = styled(Image)`
  margin: 0 auto;
  display: block;
  max-width: 160px;
  max-height: inherit;
  object-fit: contain;
`;

const mobileStreamTitle = media.phone`
  font-size: 14px;
  line-height: 20px;
  ${truncateMultiline(2)};
`;
const mobileStreamDescription = media.phone`
  font-size: 13px;
  margin-top: 8px;
`;

export const TitleAndDescription = styled.div`
  grid-area: title;
  white-space: pre-wrap;

  > div {
    max-width: 100%;
  }
`;

export const InnerTitleContent = styled.div`
  display: inline-block;
  textarea {
    padding-right: 0;
  }
`;

export const StreamTitle = styled(TitleBlock)`
  h1,
  h2,
  h3,
  h4,
  h4,
  h6 {
    color: #292a2b;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
    ${truncateMultiline(1)};
    ${mobileStreamTitle};
  }
  ${TitleDescription} {
    color: var(--gray_400);
    margin-top: 10px;
    margin-left: 5px;
    line-height: 20px;
    word-break: break-all;
    ${truncateMultiline(3)};
    ${mobileStreamDescription};
  }
`;

export const StreamDateWrapper = styled.div`
  ${flexColumn};
  grid-area: time;
  color: #919eaa;
  font-size: 14px;
  margin-left: 24px;
  line-height: 21px;
  align-items: flex-end;
  justify-content: space-between;
  & > div:last-child {
    margin-top: auto;
    font-weight: 400;
  }
`;

const streamPanelCommonStyles = css`
  display: flex;
  align-items: center;
`;

export const StreamItemTopPanel = styled.div<{ hasPreview: boolean }>`
  ${flexRow};
  min-height: 128px;
  padding: 20px;
  background: var(--gray_20);
  border-radius: 10px 10px 0 0;
  display: grid;
  grid-template:
    'preview title time'
    auto / ${({ hasPreview }) => (hasPreview ? 179 : 0)}px minmax(180px, 1fr) auto;

  @media only screen and (max-width: 1120px) {
    grid-template:
      'preview title'
      'time time';
    grid-template-rows: auto;
    grid-template-columns: ${({ hasPreview }) => (hasPreview ? 184 : 0)}px calc(
        100% - ${({ hasPreview }) => (hasPreview ? 184 : 0)}px
      );
    ${StreamDateWrapper} {
      ${flexRow};
      margin: 0;
      margin-top: 20px;
      flex-direction: row-reverse;
    }
    ${StreamTitle} {
      max-width: unset;
    }
  }
`;

export const StreamItemBottomPanel = styled.div`
  gap: 20px;
  display: flex;
  flex-wrap: wrap;
  position: relative;
  padding: 20px 20px;
  justify-content: space-between;
  ${streamPanelCommonStyles};
`;

export const CardButton = styled(Button)`
  font-size: 14px;
  padding: 0 18px;
  margin-right: 10px;
`;

export const InviteButton = styled(IconButton)`
  padding: 0;
  width: 40px;
  margin-right: 10px;
  &:before {
    width: inherit;
    background-position-x: 12px;
  }
`;

export const CustomizeWebinar = styled(Button)`
  margin-right: 10px;
`;

export const ActionsWrapper = styled.div`
  ${flexRow};
  flex: 1;
  align-items: center;
`;

const infoWrapperMobile = media.phone`
  ${flexColumn};
  max-height: unset;
`;

export const InfoWrapper = styled.div`
  ${flexRow};
  flex: 10;
  max-height: 23px;
  min-width: fit-content;

  ${infoWrapperMobile};
`;

const cardPhoneLayout = media.phone<{ hasPreview: boolean }>`
  min-width: unset;
  width: calc(100vw - 40px);

  ${StreamItemTopPanel}{
    padding: 12px;
    grid-template:
      'preview title'
      '. time';
    grid-template-columns: ${({ hasPreview }) => (hasPreview ? 100 : 0)}px calc(100% - 100px);
  }

  ${StreamItemBottomPanel} {
    padding: 12px 12px 30px;
  }

  ${StreamDateWrapper} {
    ${flexColumn};
    margin-top: 16px;
    align-items: flex-start;
  }

  ${PreviewWrapper} {
    margin-right: 10px;
  }

  ${Preview} {
    width: 90px;
    height: 51px;
  }

  ${InfoWrapper} {
    margin: 0;
    width: 100%;
  }

  ${ActionsWrapper} {
    width: 100%;
  }

  ${CustomizeWebinar} {
    margin-top: 0;
  }
`;

const mobileDropdown = media.phone`
  top: 0;
`;

export const DropdownActions = styled.div`
  & > div:hover:before {
    ${mobileDropdown};
  }
`;

export const Wrapper = styled.div<{ hasPreview: boolean }>`
  width: 100%;
  ${flexColumn};
  max-width: 920px;
  min-width: 400px;
  border-radius: 10px;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 56, 87, 0.2);
  ${cardPhoneLayout};
`;
